import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, Shield, Star, Zap, Crown } from 'lucide-react';
import EventCard from '../components/events/EventCard';
import SectionHeader from '../components/events/SectionHeader';
import { PageContainer } from '../components/shared';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiBaseUrl } from '../utils/apiUrl';

const EventsPage = () => {
  const { t, language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('category'); // 'category' or 'weekly'
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Fetch events from API
  const fetchEvents = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      // Public calendar should show all events, no pagination
      const params = new URLSearchParams({
        page: '1',
        limit: '1000', // Large limit to get all events
        month: currentMonth.toString(),
        year: currentYear.toString(),
        sortBy: 'start_date',
        sortOrder: 'asc'
      });

      const response = await fetch(`${getApiBaseUrl()}/api/events?${params}`, {
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Filter and validate events - ensure they have required fields and are not category objects
        const rawEvents = data.data.events || data.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const validEvents = rawEvents.filter(event => {
          // Skip null/undefined
          if (!event || typeof event !== 'object' || Array.isArray(event)) return false;
          
          // Reject objects that look like category objects (have category-specific fields)
          // BUT: events can have name_en/name_th and description_en/description_th (they're event fields too)
          // Category objects have: has_bonuses, default_start_time, is_weekly, day_of_week, is_monthly, is_multi_day
          // AND they DON'T have: start_date, end_date, category, sub_category
          const hasCategoryFields = event.has_bonuses !== undefined || 
                                    event.default_start_time !== undefined || 
                                    event.is_weekly !== undefined || 
                                    event.day_of_week !== undefined ||
                                    event.is_monthly !== undefined || 
                                    event.is_multi_day !== undefined;
          
          const hasEventFields = event.start_date !== undefined || 
                                event.end_date !== undefined || 
                                event.category !== undefined ||
                                event.sub_category !== undefined;
          
          // If it has category-specific fields but NO event fields, it's a category object
          if (hasCategoryFields && !hasEventFields) {
            return false;
          }
          
          // Must have start_date to be a valid event
          if (!event.start_date) {
            return false;
          }
          
          // Filter out past events (end_date has passed)
          if (event.end_date) {
            const endDate = new Date(event.end_date);
            endDate.setHours(23, 59, 59, 999);
            if (endDate < today) {
              return false; // Event has ended
            }
          }
          
          return true;
        });
        
        // Remove debug logging to reduce console noise
        // console.log('Valid events after filtering:', validEvents.length, 'from', rawEvents.length);
        setEvents(validEvents);
        // Public calendar doesn't need pagination - show all events on one page
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: validEvents.length,
          itemsPerPage: 1000
        });
      } else {
        setError(data.error || 'Failed to fetch events');
        setEvents([]);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Network error: ' + err.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, currentYear]);

  useEffect(() => {
    fetchEvents(1);
  }, [fetchEvents]);

  // Refresh when window regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchEvents(1);
      }
    };
    const handleFocus = () => {
      fetchEvents(1);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchEvents]);

  // Get current date display (locale-aware)
  const getCurrentDateDisplay = () => {
    const today = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return today.toLocaleDateString(language === 'th' ? 'th-TH' : 'en-US', options);
  };

  // Group events by category
  const groupedByCategory = useMemo(() => {
    const grouped = {
      raid: [],
      spotlight: [],
      max_monday: [],
      event: []
    };

    events.forEach(event => {
      // Skip if this looks like a category object (not an event)
      if (!event || typeof event !== 'object' || Array.isArray(event)) return;
      
      // Category objects have: has_bonuses, default_start_time, is_weekly, day_of_week, is_monthly, is_multi_day
      // AND they DON'T have: start_date, end_date, category, sub_category
      // Note: Events CAN have name_en/name_th (they're event fields too)
      const hasCategoryFields = event.has_bonuses !== undefined || 
                                event.default_start_time !== undefined || 
                                event.is_weekly !== undefined || 
                                event.day_of_week !== undefined ||
                                event.is_monthly !== undefined || 
                                event.is_multi_day !== undefined;
      
      const hasEventFields = event.start_date !== undefined || 
                            event.end_date !== undefined || 
                            event.category !== undefined ||
                            event.sub_category !== undefined;
      
      // If it has category-specific fields but NO event fields, it's a category object
      if (hasCategoryFields && !hasEventFields) {
        return;
      }
      
      const category = event.category || 'event';
      if (grouped[category]) {
        grouped[category].push(event);
      } else {
        grouped.event.push(event);
      }
    });

    return grouped;
  }, [events]);

  // Generate weekly view
  const weeklyEvents = useMemo(() => {
    if (view !== 'weekly') return [];

    const weeks = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth, 0);
    
    // Calculate first Monday of the month (or Monday before if month doesn't start on Monday)
    // getDay() returns: 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday
    // We want Monday (1) to be the first day of the week
    const firstDayOfWeek = firstDay.getDay(); // 0=Sunday, 1=Monday, etc.
    const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // If Sunday, go back 6 days, else go back (day-1) days
    
    const firstWeekStart = new Date(firstDay);
    firstWeekStart.setDate(firstDay.getDate() - daysToSubtract);

    let currentWeekStart = new Date(firstWeekStart);
    while (currentWeekStart <= lastDay) {
      // Week runs from Monday to Sunday
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(currentWeekStart.getDate() + 6); // Monday + 6 days = Sunday
      weekEnd.setHours(23, 59, 59, 999);

      // Filter out past weeks (week has ended)
      if (weekEnd < today) {
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        continue; // Skip this week, it's in the past
      }

      const weekEvents = events.filter(event => {
        // Skip if this looks like a category object (not an event)
        if (!event || typeof event !== 'object' || Array.isArray(event)) return false;
        
        // Category objects have: has_bonuses, default_start_time, is_weekly, day_of_week, is_monthly, is_multi_day
        // AND they DON'T have: start_date, end_date, category, sub_category
        // Note: Events CAN have name_en/name_th (they're event fields too)
        const hasCategoryFields = event.has_bonuses !== undefined || 
                                  event.default_start_time !== undefined || 
                                  event.is_weekly !== undefined || 
                                  event.day_of_week !== undefined ||
                                  event.is_monthly !== undefined || 
                                  event.is_multi_day !== undefined;
        
        const hasEventFields = event.start_date !== undefined || 
                              event.end_date !== undefined || 
                              event.category !== undefined ||
                              event.sub_category !== undefined;
        
        // If it has category-specific fields but NO event fields, it's a category object
        if (hasCategoryFields && !hasEventFields) {
          return false;
        }
        
        const startDate = event.start_date ? new Date(event.start_date) : null;
        const endDate = event.end_date ? new Date(event.end_date) : null;

        if (!startDate || isNaN(startDate.getTime())) return false;
        const endOfDay = endDate ? new Date(endDate) : new Date(startDate);
        if (endDate) endOfDay.setHours(23, 59, 59, 999);

        return startDate <= weekEnd && endOfDay >= currentWeekStart;
      });

      if (currentWeekStart.getMonth() === currentMonth - 1 || weekEvents.length > 0) {
        weeks.push({
          start: new Date(currentWeekStart),
          end: weekEnd,
          events: weekEvents
        });
      }

      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }

    return weeks;
  }, [view, events, currentMonth, currentYear]);

  // Raid Section Component
  const RaidSection = () => {
    const raidEvents = groupedByCategory.raid;
    if (raidEvents.length === 0) return null;

    const megaRaids = raidEvents.filter(e => e.sub_category === 'mega');
    const fiveStarRaids = raidEvents.filter(e => e.sub_category === '5-star');
    const shadowRaids = raidEvents.filter(e => e.sub_category === 'shadow');

    const badges = [];
    if (megaRaids.length > 0) badges.push('Mega');
    if (fiveStarRaids.length > 0) badges.push('5-Star');
    if (shadowRaids.length > 0) badges.push('Shadow');

    return (
      <section className="bg-white rounded-3xl shadow-xl overflow-hidden border border-indigo-100">
        <SectionHeader
          title={t('events.raidBattles')}
          icon={Shield}
          gradient={['from-indigo-600', 'to-purple-600']}
          badges={badges}
        />
        <div className="p-6 md:p-8 bg-bg-raid">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {raidEvents.map((event) => (
              <EventCard key={event.id} event={event} variant="raid" />
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Spotlight Section Component
  const SpotlightSection = () => {
    const spotlightEvents = groupedByCategory.spotlight;
    if (spotlightEvents.length === 0) return null;

    return (
      <section className="bg-white rounded-3xl shadow-xl overflow-hidden border border-green-100 h-full">
        <SectionHeader
          title={t('events.spotlightHours')}
          subtitle={t('events.spotlightSubtitle')}
          icon={Star}
          gradient={['from-green-500', 'to-emerald-600']}
        />
        <div className="p-6 bg-bg-spot space-y-4">
          {spotlightEvents.map((event) => (
            <EventCard key={event.id} event={event} variant="spotlight" />
          ))}
        </div>
      </section>
    );
  };

  // Max Monday Section Component
  const MaxMondaySection = () => {
    const maxEvents = groupedByCategory.max_monday;
    if (maxEvents.length === 0) return null;

    return (
      <section className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 h-full">
        <SectionHeader
          title={t('events.maxMondays')}
          subtitle={t('events.maxMondaysSubtitle')}
          icon={Zap}
          gradient={['from-gray-700', 'to-gray-800']}
        />
        <div className="p-6 bg-bg-max">
          <div className="grid grid-cols-2 gap-4">
            {maxEvents.map((event) => (
              <EventCard key={event.id} event={event} variant="max_monday" />
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Events Section Component
  const EventsSection = () => {
    const eventEvents = groupedByCategory.event;
    if (eventEvents.length === 0) return null;

    return (
      <section className="bg-white rounded-3xl shadow-xl overflow-hidden border border-blue-100">
        <SectionHeader
          title={t('events.important')}
          icon={Calendar}
          gradient={['from-blue-500', 'to-sky-600']}
        />
        <div className="p-6 bg-bg-event">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {eventEvents.map((event) => (
              <EventCard key={event.id} event={event} variant="general" />
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Weekly View Component
  const WeeklyView = () => {
    return (
      <div className="space-y-8 animate-fade-in">
        {weeklyEvents.map((week, index) => (
          <section key={index} className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-100 p-4 border-b border-gray-200 font-bold text-xl text-gray-700 flex justify-between items-center">
              <span>
                <Calendar className="inline w-5 h-5 mr-2 text-blue-500" />
                สัปดาห์ที่ {index + 1} ({week.start.getDate()} {week.start.toLocaleDateString('th-TH', { month: 'short' })} - {week.end.getDate()} {week.end.toLocaleDateString('th-TH', { month: 'short' })})
              </span>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50">
              {week.events.length > 0 ? (
                week.events.map((event) => {
                  const variant = event.category === 'raid' ? 'raid' :
                                 event.category === 'spotlight' ? 'spotlight' :
                                 event.category === 'max_monday' ? 'max_monday' : 'general';
                  return (
                    <EventCard key={event.id} event={event} variant={variant} />
                  );
                })
              ) : (
                <div className="text-gray-400 italic text-center col-span-full py-4">
                  {t('events.noEvents')}
                </div>
              )}
            </div>
          </section>
        ))}
      </div>
    );
  };

  if (loading && events.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" style={{
        backgroundColor: '#f8fafc',
        backgroundImage: 'radial-gradient(#e2e8f0 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('events.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" style={{
        backgroundColor: '#f8fafc',
        backgroundImage: 'radial-gradient(#e2e8f0 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }}>
        <div className="text-center">
          <p className="text-red-600">{t('common.error')}: {error}</p>
          <button
            onClick={() => fetchEvents(1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('home.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-gray-800 antialiased min-h-screen pb-20" style={{
      backgroundColor: '#f8fafc',
      backgroundImage: 'radial-gradient(#e2e8f0 1px, transparent 1px)',
      backgroundSize: '20px 20px'
    }}>
      {/* Header */}
      <header className="pt-10 pb-6 px-4 text-center bg-white shadow-sm border-b border-gray-200 mb-8 sticky top-0 z-50 bg-opacity-95 backdrop-blur-sm">
        <div className="flex items-center justify-center gap-4">
          <img
            src="/logo-pogodex.png"
            alt="Pokemon Logo"
            className="h-12 md:h-16 drop-shadow-md filter"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="mt-2 flex flex-col md:flex-row items-center justify-center gap-2 text-sm text-gray-500 font-medium">
          <span>{t('events.calendarTitle')}{currentMonth === new Date().getMonth() + 1 && currentYear === new Date().getFullYear() ? '' : ` ${currentMonth}/${currentYear}`}</span>
          <span className="hidden md:inline">•</span>
          <div className="text-blue-600 font-bold bg-blue-50 px-3 py-1 rounded-full">
            <Clock className="inline w-4 h-4 mr-2" />
            {t('events.today')}: {getCurrentDateDisplay()}
          </div>
        </div>

        {/* View Toggle Buttons */}
        <div className="mt-6 flex justify-center gap-2">
          <motion.button
            onClick={() => setView('category')}
            className={`px-6 py-2 rounded-full font-semibold transition-all duration-200 flex items-center gap-2 ${
              view === 'category'
                ? 'bg-poke-blue text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Crown className="w-4 h-4" />
            {t('events.byCategory')}
          </motion.button>
          <motion.button
            onClick={() => setView('weekly')}
            className={`px-6 py-2 rounded-full font-semibold transition-all duration-200 flex items-center gap-2 ${
              view === 'weekly'
                ? 'bg-poke-blue text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Calendar className="w-4 h-4" />
            {t('events.byWeek')}
          </motion.button>
        </div>
      </header>

      <PageContainer maxWidth="7xl" padding={true} className="py-8">
        <AnimatePresence mode="wait">
          {view === 'category' ? (
            <motion.div
              key="category"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-10 transition-opacity duration-300"
            >
              <RaidSection />
              
              {/* Grid for Spotlight & Max */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <SpotlightSection />
                <MaxMondaySection />
              </div>

              <EventsSection />
            </motion.div>
          ) : (
            <motion.div
              key="weekly"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <WeeklyView />
            </motion.div>
          )}
        </AnimatePresence>
      </PageContainer>
    </div>
  );
};

export default EventsPage;
